import React from 'react';
import useStores from '@stores/useStores';
import Button from '@ui-library/button';
import { getApiErrorMessage } from '@components/common/util/util';
import useModalView from '@hooks/useModalView';
import Modal from '@ui-library/modal/custom';
import { LabelMedium } from '@ui-library/typography';
import { CatchErr } from '@utils/types';
import { isValidationLoadingType } from '../../addSource';
import classes from '../common.module.scss';

interface AccountActionsProps {
  role: string;
  refreshAccounts: () => Promise<void>;
  selectedAccountId?: string;
  isViewMode: boolean;
  validationState?: string;
  setViewMode: (enableViewMode: boolean) => void;
  hideDelete: boolean;
}

const AccountActions: React.FC<AccountActionsProps> = ({
  role,
  refreshAccounts,
  selectedAccountId,
  setViewMode,
  isViewMode,
  hideDelete,
  validationState,
}: AccountActionsProps) => {
  const [deleteModal, showDeleteModal] = useModalView(
    <Modal
      title={<div>Hold up!</div>}
      content={<LabelMedium secondary>Are you sure you want to remove this account?</LabelMedium>}
      onClose={() => showDeleteModal(false)}
      saveLabel="Yes, delete account"
      saveLabelBtnType="deletePrimary"
      isSaveDisabled={!selectedAccountId}
      onSave={() => {
        if (selectedAccountId) {
          onDelete(selectedAccountId);
        }
      }}
    />,
  );
  const { messagesStore, sourceDataStore } = useStores();

  const onDelete = React.useCallback(
    async (id: string) => {
      try {
        await sourceDataStore.deleteAccount(id);
        await refreshAccounts();
        setViewMode(false);
      } catch (err: CatchErr) {
        messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to delete the account'));
      }
      showDeleteModal(false);
    },
    [role],
  );
  return (
    <div className={classes.account_actions__wrapper}>
      {deleteModal}
      {isViewMode && (
        <Button
          name="Back"
          secondary
          onClick={() => {
            setViewMode(false);
          }}
        />
      )}
      <div className={classes.account_actions}>
        {!isViewMode && (
          <Button
            tooltip="To view the credential details, click on any of the accounts below."
            disabled={!selectedAccountId}
            secondary
            name="View"
            onClick={() => {
              setViewMode(true);
            }}
          />
        )}
        {hideDelete ? null : (
          <Button
            tooltip={
              validationState === isValidationLoadingType.loading
                ? 'Please wait for the validation to finish'
                : 'To delete an account, click on any of the accounts below.'
            }
            deleteOutlined
            className={classes.delete__button}
            name="Delete"
            disabled={!selectedAccountId || validationState === isValidationLoadingType.loading}
            onClick={() => {
              showDeleteModal(true);
            }}
          />
        )}
      </div>
    </div>
  );
};
export default AccountActions;
